
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { DialogContentText, Stack } from "@mui/material";
import { addDays } from "date-fns";
import _ from "lodash";
import React, { useEffect, useMemo, useState, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IDateRange } from "../../common/models/IDateRange";
import FormDateRange from "../forms/form-date-range/FormDateRange";
import FormRadioGroup, { FormRadioGroupRadioButton } from "../forms/form-radio-group/FormRadioGroup";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import { DurationOptionEnum, durationRadioButtons } from "./CustomDurationUtils";

export type CustomDurationDialogValues = {
    option: DurationOptionEnum;
    duration: IDateRange;
};

export type CustomDurationDialogResult = DialogResult<CustomDurationDialogValues>;

export type CustomDurationDialogProps = {
    contentText?: string | null;
    overrideDefaultText?: string | null;
    hideDefault?: boolean,
    value: CustomDurationDialogValues;
    onDurationChanged?: (result: DialogResult<CustomDurationDialogValues>) => void;
} & DialogProps<CustomDurationDialogValues, CustomDurationDialogResult>;


type CustomDurationDialogFormValuesType = {
    option: string;
    duration: IDateRange;
};

export function CustomDurationDialog({
    open,
    onClose,
    contentText,
    overrideDefaultText,
    hideDefault,
    value
}: CustomDurationDialogProps): JSX.Element {

    const { t } = useTranslation();

    const form = useForm<CustomDurationDialogFormValuesType>({
        mode: "onChange",
        defaultValues: useMemo(() => {
            return {
                option: value.option.toString(),
                duration: {
                    startDate: value.duration.startDate,
                    endDate: value.duration.endDate
                } as IDateRange
            } as CustomDurationDialogFormValuesType;
        }, [value])
    });

    const [options] = useState<FormRadioGroupRadioButton[]>(() => {
        const data = _.cloneDeep(durationRadioButtons);
        if (overrideDefaultText) {
            data[0].label = overrideDefaultText;
        }
        if (hideDefault) {
            data.shift();
        }

        return data;
    });

    const {
        handleSubmit,
        formState: {
            //errors,
            isDirty,
            isValid
        },
        getValues,
        setValue,
        watch,
        reset
    } = form;


    const onOk = (values: CustomDurationDialogFormValuesType) => {
        //const values = getValues();

        const dialogValues: CustomDurationDialogValues = {
            option: +values.option as DurationOptionEnum,
            duration: {
                startDate: addDays(new Date(), -7),
                endDate: new Date()
            }
        };

        switch (dialogValues.option) {
            case DurationOptionEnum.Default:
                break;

            case DurationOptionEnum.OneDay:
            case DurationOptionEnum.ThreeDays:
            case DurationOptionEnum.SevenDays:
            case DurationOptionEnum.ThirtyDays:
                const today = new Date();
                dialogValues.duration.startDate = addDays(today, -(+dialogValues.option));
                dialogValues.duration.endDate = today;

                break;

            case DurationOptionEnum.Custom:
                dialogValues.duration.startDate = values.duration.startDate;
                dialogValues.duration.endDate = values.duration.endDate;
                break;
        }

        reset();

        onClose && onClose({
            reason: "ok",
            values: dialogValues
        });
    };


    const onCancel = () => {
        reset();

        onClose && onClose({
            reason: "cancel",
            values: undefined
        });
    };

    useEffect(() => {
        reset({
            option: value.option.toString(),
            duration: {
                startDate: value.duration.startDate,
                endDate: value.duration.endDate
            }

        } as CustomDurationDialogFormValuesType);
    }, [value, reset]);

    // the following caused re-renders which affects the new date picker control.
    // It loses focus on the time parts
    //const watchAll = watch();

    // useEffect(() => {
    //     if (+watchAll.option !== DurationOptionEnum.Custom) {

    //         const endDate = watchAll.duration.endDate ?? new Date();

    //         if (!watchAll.duration.endDate) {
    //             setValue('duration.endDate', endDate);
    //         }

    //         if (!watchAll.duration.startDate) {
    //             setValue('duration.startDate', addDays(endDate, -7));
    //             //setFocus('startDate')
    //         }

    //     }

    // }, [watchAll, setValue]);

    const option = +watch('option');

   useEffect(() => {
        if (option !== DurationOptionEnum.Custom) {
            const values = getValues();


            const endDate = values.duration.endDate ?? new Date();

            if (!values.duration.endDate) {
                setValue('duration.endDate', endDate);
            }

            if (!values.duration.startDate) {
                setValue('duration.startDate', addDays(endDate, -7));
                //setFocus('startDate')
            }

        }

    }, [option, 
        getValues,
        setValue]);


    return (

        <ResponsiveDialog
            title={t('customDuration.title')}
            enableOkay={isDirty && isValid}
            okayLabel={t("buttons.okay")}
            open={open}
            onClose={
                (reason: string) => {
                    reason === "ok"
                        //? handleSubmit(async (data) => onOk(data))()
                        ? handleSubmit((data) => onOk(data))()
                        : onCancel();
                }
            }>
            {contentText &&
                <DialogContentText>
                    {contentText}
                </DialogContentText>
            }

            <Form form={form}>
                <Stack spacing={2}>
                    <FormRadioGroup label=''
                        name='option'
                        radioButtons={options} />

                    <FormDateRange
                        name='duration'
                        //disabled={watchAll.option !== DurationOptionEnum.Custom.toString()}
                        disabled={option !== DurationOptionEnum.Custom}
                        spacing={{ xs: 2, sm: 4 }}
                    />
                </Stack>
            </Form>


        </ResponsiveDialog>



    );

}

export type CustomDurationDialogState = DialogState<CustomDurationDialogValues, CustomDurationDialogResult>;
export type BindCustomDurationDialogReturn = BindDialogReturn<CustomDurationDialogValues, CustomDurationDialogResult>;

export function useCustomDurationDialogState(): CustomDurationDialogState {
    return useDialogState();
}

export function bindCustomDurationDialog(props: CustomDurationDialogState): BindCustomDurationDialogReturn {
    return bindDialog(props);
}


